import type { TouchEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useBranchLabels } from '../widget-hooks/useBranchLabels';
import {
  fetchScheduleSlots,
  fetchScheduleSlotsAllBranches,
  type ScheduleSlotListItem
} from '../widget-api/schedule-slots';
import { fetchClientServices } from '../widget-api/client-services';
import { useAsync } from '../widget-hooks/useAsync';
import { loadBranchFilterPrefs, saveBranchFilterPrefs } from '../widget-lib/embed-user-prefs';
import { canonicalizeScheduleTrainerQuery, readScheduleTrainerUserIdQuery } from '../widget-lib/schedule-trainer-query';
import { resolveSlotAccent } from '../widget-lib/slot-color';
import { MediaAvatar } from '../widget-ui/media-avatar';
import { PATH, pathSession } from '../widget-lib/path-routes';
import { BranchPickLink } from '../widget-ui/branch-pick-link';
import { PromoBanner } from '../widget-ui/promo-banner';
import { nextDayWithFreeSlots, stripOffsetForDay } from '../widget-lib/schedule-days';
import { isClientPathEnabledByModules } from '../widget-lib/embed-client-module-gates';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';

import {
  FilterIcon,
  ScheduleFiltersModal,
  type ScheduleFiltersApplyPayload
} from './schedule_filters-modal';

const MAX_RANGE_DAYS = 60;

/** Чипы фильтров расписания — тот же ритм, что pill-меню в шапке (11px, semibold, px-3, py-1.5). */
const SCHEDULE_FILTER_CHIP_CLASS =
  'max-w-[min(100%,20rem)] truncate rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-1.5 text-[11px] font-semibold leading-tight text-[var(--text)] shadow-sm';

/** Бейдж «места» в карточке слота (компактная pill по центру ряда). */
const SCHEDULE_PLACES_CHIP_BASE =
  'max-w-[min(100%,11rem)] shrink-0 truncate rounded-full px-2.5 py-1 text-center text-[11px] font-semibold leading-tight';

/** В сети с несколькими филиалами в ссылку на /sessions/:id подставляем branch_id, чтобы на экране записи не спрашивать филиал снова. */
function sessionSearchWithSlotBranch(
  currentQs: string,
  slot: ScheduleSlotListItem,
  isMultiBranch: boolean
): string {
  if (!isMultiBranch || slot.branch_id == null) {
    return currentQs;
  }
  const p = new URLSearchParams(currentQs);
  p.set('branch_id', String(slot.branch_id));
  return p.toString();
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isIsoDate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

export function clampRange(from: string, to: string): { fromDate: string; toDate: string } {
  let fromD = new Date(`${from}T12:00:00`);
  let toD = new Date(`${to}T12:00:00`);
  if (fromD > toD) {
    const t = fromD;
    fromD = toD;
    toD = t;
  }
  const ms = toD.getTime() - fromD.getTime();
  const days = Math.round(ms / 86400000);
  if (days > MAX_RANGE_DAYS) {
    const end = new Date(fromD);
    end.setDate(end.getDate() + MAX_RANGE_DAYS);
    return { fromDate: toIsoDate(fromD), toDate: toIsoDate(end) };
  }
  return { fromDate: toIsoDate(fromD), toDate: toIsoDate(toD) };
}

/** Период из query или 14 дней от сегодня. */
export function resolveScheduleRange(scheduleFrom: string | null, scheduleTo: string | null): {
  fromDate: string;
  toDate: string;
} {
  const today = new Date();
  const defaultFrom = toIsoDate(today);
  const defaultTo = toIsoDate(addDays(today, 14));
  const from = scheduleFrom && isIsoDate(scheduleFrom) ? scheduleFrom : defaultFrom;
  const to = scheduleTo && isIsoDate(scheduleTo) ? scheduleTo : defaultTo;
  return clampRange(from, to);
}

export function listIsoDaysInRange(fromDate: string, toDate: string): string[] {
  const out: string[] = [];
  const cur = new Date(`${fromDate}T12:00:00`);
  const end = new Date(`${toDate}T12:00:00`);
  for (; cur <= end; cur.setDate(cur.getDate() + 1)) {
    out.push(toIsoDate(cur));
  }
  return out;
}

function groupByDate(slots: ScheduleSlotListItem[]): Map<string, ScheduleSlotListItem[]> {
  const map = new Map<string, ScheduleSlotListItem[]>();
  const sorted = [...slots].sort((a, b) => {
    const da = `${a.slot_date}T${a.start_time}`;
    const db = `${b.slot_date}T${b.start_time}`;
    return da.localeCompare(db);
  });
  for (const s of sorted) {
    const list = map.get(s.slot_date) ?? [];
    list.push(s);
    map.set(s.slot_date, list);
  }
  return map;
}

function formatDayLabel(isoDate: string): string {
  try {
    const d = new Date(`${isoDate}T12:00:00`);
    return d.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch {
    return isoDate;
  }
}

function formatShortWeekday(isoDate: string): string {
  try {
    const d = new Date(`${isoDate}T12:00:00`);
    return d.toLocaleDateString('ru-RU', { weekday: 'short' });
  } catch {
    return '';
  }
}

/** Короткий день недели для ленты дат (как в макете: «вт», «ср»). */
function formatShortWeekdayLower(isoDate: string): string {
  return formatShortWeekday(isoDate).toLocaleLowerCase('ru-RU');
}



function formatPeriodShort(from: string, to: string): string {
  try {
    const a = new Date(`${from}T12:00:00`);
    const b = new Date(`${to}T12:00:00`);
    const o: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    return `${a.toLocaleDateString('ru-RU', o)} — ${b.toLocaleDateString('ru-RU', o)}`;
  } catch {
    return `${from} — ${to}`;
  }
}

/** Текст бейджа остатка мест (когда задан лимит участников). */
function scheduleSlotPlacesLabel(slot: ScheduleSlotListItem): string | null {
  if (slot.max_participants <= 0) return null;
  const n = slot.places_left;
  if (n <= 0) return 'Запись закрыта';
  if (n === 1) return 'Осталось одно место';
  if (n >= 2 && n <= 5) return 'Осталось несколько мест';
  return 'Много мест';
}

/** Класс бейджа остатка мест: «Много мест» — светлый акцент и синий текст по референсу. */
function scheduleSlotPlacesBadgeClass(slot: ScheduleSlotListItem): string {
  if (slot.max_participants <= 0) {
    return 'border border-[var(--border)] bg-[var(--bg-soft)] text-[var(--text-muted)]';
  }
  if (slot.places_left <= 0) {
    return 'border border-[var(--border)] bg-[var(--bg-soft)] text-[var(--text-muted)]';
  }
  return [
    'border',
    'border-[color-mix(in_srgb,var(--accent)_32%,var(--border))]',
    'bg-[color-mix(in_srgb,var(--accent)_11%,var(--bg-elevated))]',
    'text-[var(--accent)]'
  ].join(' ');
}

/** Полоска типа/цвета слева у карточки слота. */
function SlotAccentStripe({ slot }: { slot: ScheduleSlotListItem }) {
  const accent = resolveSlotAccent(slot.service_type_color, slot.service_color);
  const stripe = accent ?? 'var(--accent)';
  return (
    <div
      className="w-1.5 shrink-0 self-stretch rounded-l-2xl sm:w-2"
      style={{ backgroundColor: stripe }}
      aria-hidden
    />
  );
}

/** Контент карточки слота (сетка время · бейдж · цена, название, тренер/филиал) — общий для списка и превью «ближайшее». */
function ScheduleSlotCardInnerBody({ slot }: { slot: ScheduleSlotListItem }) {
  const startTime = (slot.start_time || '').slice(0, 5);
  const endTime = (slot.end_time || '').slice(0, 5);
  const placesText = scheduleSlotPlacesLabel(slot);
  const trainerLine = slot.trainer_name?.trim() || (slot.is_rental ? 'Аренда' : null);
  const branchLine = slot.branch_label?.trim() || null;
  const metaSub =
    trainerLine && branchLine ? `${trainerLine} · ${branchLine}` : trainerLine || branchLine || null;

  return (
    <>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-2 gap-y-1">
        <p className="min-w-0 justify-self-start text-sm tabular-nums text-[var(--text)]">
          {slot.is_all_day ? (
            <span className="font-bold">Весь день</span>
          ) : (
            <>
              <span className="font-bold">{startTime}</span>
              {endTime ? <span className="font-normal text-[var(--text-muted)]"> — {endTime}</span> : null}
            </>
          )}
        </p>
        <div className="flex justify-center justify-self-center px-0.5">
          {placesText != null ? (
            <span className={[SCHEDULE_PLACES_CHIP_BASE, scheduleSlotPlacesBadgeClass(slot)].join(' ')}>
              {placesText}
            </span>
          ) : null}
        </div>
        <div className="min-w-0 justify-self-end text-right">
          {slot.service_price != null ? (
            <span className="text-sm font-bold tabular-nums text-[var(--text)]">
              {slot.service_price.toLocaleString('ru-RU')} ₽
            </span>
          ) : (
            <span className="text-xs text-[var(--text-muted)]">По запросу</span>
          )}
        </div>
      </div>
      <p className="mt-3 text-base font-bold leading-snug text-[var(--text)] line-clamp-2">{slot.service_name}</p>
      {metaSub ?     <p className="mt-1.5 truncate text-xs text-[var(--text-muted)]">{metaSub}</p> : null}
      {trainerLine ? (
        <div className="mt-2 flex items-center gap-2">
          <MediaAvatar
            photoUrl={slot.trainer_photo_url}
            label={trainerLine}
            sizeClass="h-6 w-6"
          />
          <span className="truncate text-xs text-[var(--text-muted)]">{trainerLine}{branchLine ? ` · ${branchLine}` : null}</span>
        </div>
      ) : branchLine ? (
        <p className="mt-1.5 truncate text-xs text-[var(--text-muted)]">{branchLine}</p>
      ) : null}
    </>
  );
}

/** Карточка слота — макет: толстая цветная полоска, ряд «время · бейдж · цена», название, тренер. */
function ScheduleSlotCardCompact({
  slot,
  qs,
  isMultiBranch
}: {
  slot: ScheduleSlotListItem;
  qs: string;
  isMultiBranch: boolean;
}) {
  const sessionQs = sessionSearchWithSlotBranch(qs, slot, isMultiBranch);

  return (
    <Link
      to={{ pathname: pathSession(slot.id), search: sessionQs }}
      state={{ slot }}
      className="flex min-w-0 overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-[var(--shadow-card)] transition-shadow duration-200 hover:shadow-md active:scale-[0.99]"
    >
      <SlotAccentStripe slot={slot} />
      <div className="min-w-0 flex-1 px-4 py-4">
        <ScheduleSlotCardInnerBody slot={slot} />
      </div>
    </Link>
  );
}

export default function SchedulePage() {
  useWidgetSeo(
    'Расписание занятий',
    'Актуальное расписание занятий и тренировок. Удобный выбор даты, времени, тренера и онлайн-запись.'
  );
  const navigate = useNavigate();
  const suffix = useQuerySuffix();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const dayStripRef = useRef<HTMLDivElement | null>(null);
  const lastClickTimeRef = useRef(0);
  const [visibleCount, setVisibleCount] = useState(5);
  const [stripOffset, setStripOffset] = useState(0);

  const scrollStripByPage = (dir: -1 | 1) => {
    const now = Date.now();
    if (now - lastClickTimeRef.current < 400) {
      return;
    }
    lastClickTimeRef.current = now;

    const vc = visibleCount;
    const total = stripDays.length;
    const step = Math.min(vc, total);

    if (dir === -1) {
      if (stripOffset <= 0) {
        shiftScheduleRangeByDays(-step);
        return;
      }
      setStripOffset(Math.max(0, stripOffset - step));
    } else {
      if (stripOffset + vc >= total) {
        setStripOffset(0);
        shiftScheduleRangeByDays(step);
        return;
      }
      setStripOffset(Math.min(total - vc, stripOffset + step));
    }
  };

  useLayoutEffect(() => {
    canonicalizeScheduleTrainerQuery(searchParams, setSearchParams);
  }, [searchParams, setSearchParams]);

  const qs = searchParams.toString();

  const serviceIdRaw = searchParams.get('service_id');
  const parsedServiceId =
    serviceIdRaw != null && serviceIdRaw !== '' ? Number.parseInt(serviceIdRaw, 10) : NaN;
  const filterServiceId =
    Number.isFinite(parsedServiceId) && parsedServiceId > 0 ? parsedServiceId : null;

  const filterTrainerUserId = useMemo(() => readScheduleTrainerUserIdQuery(searchParams), [searchParams]);

  const scheduleFromParam = searchParams.get('schedule_from');
  const scheduleToParam = searchParams.get('schedule_to');

  const {
    orgSlug,
    branchId,
    widgetConfig,
    guestShowcaseOrg,
    organizationId,
    accessToken,
    widgetContour,
    effectiveModules,
    loading: bootLoading,
    error: bootError,
    bootstrapHint
  } = useEmbedBootstrap();

  const isBookingContour = widgetContour === 'booking';
  /** Мобильное приложение, клиентский мини-сайт и booking-виджет — один экран расписания с лентой дней и фильтром. */
  const isCompactScheduleUi =
    widgetContour === 'mobile' || widgetContour === 'client' || widgetContour === 'booking';

  const branchLabels = useBranchLabels();

  const guestBranches = useMemo(
    () => guestShowcaseOrg?.branches ?? [],
    [guestShowcaseOrg]
  );

  const range = useMemo(
    () => resolveScheduleRange(scheduleFromParam, scheduleToParam),
    [scheduleFromParam, scheduleToParam]
  );

  const isMultiBranch = Boolean(
    (widgetConfig?.branches && widgetConfig.branches.length > 1) ||
    (guestShowcaseOrg?.branches && guestBranches.length > 1)
  );
  const allBranchesMode = isMultiBranch && searchParams.get('all_branches') === '1';

  const scheduleBranchList = useMemo(
    () =>
      widgetConfig != null
        ? (widgetConfig.branches ?? []).map((b) => ({ id: b.id, name: b.name }))
        : guestShowcaseOrg != null
          ? guestBranches.map((b) => ({ id: b.id, name: b.name }))
          : [],
    [widgetConfig, guestShowcaseOrg, guestBranches]
  );

  const needsBranch =
    (widgetConfig?.branches && widgetConfig.branches.length > 1 && branchId == null && !allBranchesMode) ||
    (widgetConfig == null && guestBranches.length > 1 && branchId == null && !allBranchesMode);

  const effectiveBranchId = useMemo(() => {
    if (allBranchesMode) {
      if (widgetConfig?.branches && widgetConfig.branches.length > 0) {
        return widgetConfig.branches[0]?.id ?? null;
      }
      if (guestShowcaseOrg && guestBranches.length > 0) {
        return guestBranches[0]?.id ?? null;
      }
      return null;
    }
    if (widgetConfig) {
      const wcBranches = widgetConfig.branches;
      const wcBranchCount = wcBranches?.length ?? 0;
      if (wcBranchCount <= 1) {
        return wcBranchCount === 1 ? wcBranches[0].id : branchId;
      }
      return branchId;
    }
    if (guestShowcaseOrg) {
      if (guestBranches.length <= 1) {
        return guestBranches[0]?.id ?? branchId;
      }
      return branchId;
    }
    return branchId;
  }, [allBranchesMode, widgetConfig, guestShowcaseOrg, guestBranches, branchId]);

  const branchPrefsHydratedRef = useRef(false);

  useEffect(() => {
    branchPrefsHydratedRef.current = false;
  }, [organizationId]);

  useLayoutEffect(() => {
    if (branchPrefsHydratedRef.current) return;
    if (organizationId == null || organizationId <= 0) return;
    if (!isMultiBranch) return;
    if (scheduleBranchList.length < 2) return;
    if (branchId != null || allBranchesMode) {
      branchPrefsHydratedRef.current = true;
      return;
    }

    const prefs = loadBranchFilterPrefs(organizationId);
    if (!prefs) {
      branchPrefsHydratedRef.current = true;
      return;
    }
    const validIds = new Set(scheduleBranchList.map((b) => b.id));

    if (prefs.allBranches) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('all_branches', '1');
        next.delete('branch_id');
        return next;
      }, { replace: true });
      branchPrefsHydratedRef.current = true;
      return;
    }
    if (prefs.branchId != null && validIds.has(prefs.branchId)) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('all_branches');
        next.set('branch_id', String(prefs.branchId));
        return next;
      }, { replace: true });
      branchPrefsHydratedRef.current = true;
      return;
    }
    if (prefs.defaultBranchId != null && validIds.has(prefs.defaultBranchId)) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('all_branches');
        next.set('branch_id', String(prefs.defaultBranchId));
        return next;
      }, { replace: true });
      branchPrefsHydratedRef.current = true;
      return;
    }
    branchPrefsHydratedRef.current = true;
  }, [
    organizationId,
    isMultiBranch,
    branchId,
    allBranchesMode,
    scheduleBranchList,
    setSearchParams,
  ]);

  const canLoadSlots =
    organizationId != null &&
    !needsBranch &&
    (allBranchesMode ||
      effectiveBranchId != null ||
      (widgetConfig != null && (widgetConfig.branches?.length ?? 0) === 0) ||
      (guestShowcaseOrg != null && guestBranches.length === 0) ||
      (widgetConfig == null && guestShowcaseOrg == null && accessToken != null));

  const canLoadServices =
    organizationId != null &&
    !needsBranch &&
    (effectiveBranchId != null ||
      (widgetConfig != null && (widgetConfig.branches?.length ?? 0) === 0) ||
      (guestShowcaseOrg != null && guestBranches.length === 0) ||
      (widgetConfig == null && guestShowcaseOrg == null && accessToken != null));

  const { data: allServices } = useAsync(
    () => {
      if (organizationId == null || !canLoadServices) return Promise.resolve([]);
      return fetchClientServices({ organizationId, branchId: effectiveBranchId, accessToken });
    },
    [organizationId, effectiveBranchId, accessToken, canLoadServices]
  );
  const serviceNameById = useMemo(() => {
    const m = new Map<number, string>();
    if (allServices) for (const s of allServices) m.set(s.id, s.name);
    return m;
  }, [allServices]);
  const filterServiceName = filterServiceId != null
    ? (() => {
        const raw = serviceNameById.get(filterServiceId);
        if (!raw) return null;
        return raw.length > 50 ? `${raw.slice(0, 50)}…` : raw;
      })()
    : null;

  const { data: slots, loading: slotsLoading, error: slotsError, reload: reloadSlots } = useAsync(
    () => {
      if (organizationId == null || needsBranch) {
        return Promise.resolve([]);
      }
      if (allBranchesMode && scheduleBranchList.length > 1) {
        return fetchScheduleSlotsAllBranches({
          organizationId: accessToken ? null : organizationId,
          branchList: scheduleBranchList,
          fromDate: range.fromDate,
          toDate: range.toDate,
          accessToken,
          serviceId: filterServiceId
        });
      }
      return fetchScheduleSlots({
        organizationId: accessToken ? null : organizationId,
        branchId: effectiveBranchId,
        fromDate: range.fromDate,
        toDate: range.toDate,
        accessToken,
        serviceId: filterServiceId
      });
    },
    [
      organizationId,
      needsBranch,
      allBranchesMode,
      scheduleBranchList,
      effectiveBranchId,
      range.fromDate,
      range.toDate,
      accessToken,
      filterServiceId
    ]
  );

  const filterTrainerName = useMemo(() => {
    if (filterTrainerUserId == null) return null;
    const s = slots?.find((x) => x.trainer_user_id === filterTrainerUserId);
    return s?.trainer_name || `Тренер #${filterTrainerUserId}`;
  }, [slots, filterTrainerUserId]);

  const futureSlots = useMemo(() => {
    const now = new Date();
    let list = slots ?? [];
    if (filterTrainerUserId != null) {
      list = list.filter((s) => s.trainer_user_id === filterTrainerUserId);
    }
    return list.filter((s) => {
      const end = `${s.slot_date}T${(s.end_time || s.start_time || '23:59').slice(0, 5)}:00`;
      try {
        return new Date(end) > now;
      } catch {
        return true;
      }
    });
  }, [slots, filterTrainerUserId]);

  const futureByDate = useMemo(() => groupByDate(futureSlots), [futureSlots]);

  const stripDays = useMemo(
    () => listIsoDaysInRange(range.fromDate, range.toDate).slice(0, 31),
    [range.fromDate, range.toDate]
  );

  const visibleDays = useMemo(
    () => stripDays.slice(stripOffset, stripOffset + visibleCount),
    [stripDays, stripOffset, visibleCount]
  );

  const [pickedIsoDay, setPickedIsoDay] = useState<string>(() => {
    const fromQs = searchParams.get('schedule_day');
    const today = toIsoDate(new Date());
    if (fromQs && isIsoDate(fromQs)) {
      return fromQs;
    }
    return today;
  });

  const dayQs = searchParams.get('schedule_day');
  useEffect(() => {
    if (dayQs && isIsoDate(dayQs)) {
      setPickedIsoDay(dayQs);
    }
  }, [dayQs]);

  useLayoutEffect(() => {
    if (visibleDays.length > 0 && !visibleDays.includes(pickedIsoDay)) {
      setPickedIsoDay(visibleDays[0]);
    }
  }, [visibleDays, pickedIsoDay]);

  useLayoutEffect(() => {
    setStripOffset(0);
  }, [stripDays]);

  useLayoutEffect(() => {
    const el = dayStripRef.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;
    const calc = () => {
      setVisibleCount(Math.max(1, Math.floor((parent.clientWidth - 88) / 60)));
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [stripDays]);

  const initialSyncRef = useRef(false);
  useLayoutEffect(() => {
    if (!isCompactScheduleUi) return;
    if (initialSyncRef.current) return;
    initialSyncRef.current = true;
    const qs = searchParams.get('schedule_day');
    if (qs && isIsoDate(qs) && qs === pickedIsoDay) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('schedule_day', pickedIsoDay);
        return next;
      },
      { replace: true }
    );
  }, [isCompactScheduleUi, pickedIsoDay, searchParams, setSearchParams]);

  const pickDay = useCallback(
    (day: string) => {
      setPickedIsoDay(day);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('schedule_day', day);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const slotsForPickedDay = useMemo(() => {
    const list = futureByDate.get(pickedIsoDay) ?? [];
    return list.filter((s) => s.places_left > 0);
  }, [futureByDate, pickedIsoDay]);

  const nextFreeDay = useMemo(() => nextDayWithFreeSlots(futureByDate, pickedIsoDay), [futureByDate, pickedIsoDay]);

  const jumpToDay = useCallback(
    (day: string) => {
      const offset = stripOffsetForDay(stripDays, day, visibleCount);
      if (offset != null) setStripOffset(offset);
      pickDay(day);
    },
    [stripDays, visibleCount, pickDay]
  );

  const openTrainers = () => {
    if (!accessToken) {
      navigate(`${PATH.signIn}${suffix}`, { state: { from: `${PATH.trainers}${suffix}` } });
      return;
    }
    navigate(`${PATH.trainers}${suffix}`);
  };

  /** Свайп по ленте дней: влево — следующие дни, вправо — предыдущие. */
  const swipeStartXRef = useRef<number | null>(null);
  const onDayStripTouchStart = (e: TouchEvent) => {
    swipeStartXRef.current = e.touches[0]?.clientX ?? null;
  };
  const onDayStripTouchEnd = (e: TouchEvent) => {
    const startX = swipeStartXRef.current;
    swipeStartXRef.current = null;
    const endX = e.changedTouches[0]?.clientX;
    if (startX == null || endX == null) return;
    const dx = endX - startX;
    if (Math.abs(dx) < 40) return;
    scrollStripByPage(dx < 0 ? 1 : -1);
  };



  const hasActiveCustomDates =
    scheduleFromParam != null || scheduleToParam != null;

  function applyScheduleFilters(p: ScheduleFiltersApplyPayload) {
    const clamped = clampRange(p.fromDate, p.toDate);
    const multiBranch =
      (widgetConfig?.branches && widgetConfig.branches.length > 1) ||
      (guestShowcaseOrg?.branches && guestBranches.length > 1);
    if (multiBranch && organizationId != null && organizationId > 0) {
      if (p.allBranches) {
        saveBranchFilterPrefs(organizationId, { branchId: null, allBranches: true });
      } else if (p.branchId != null) {
        saveBranchFilterPrefs(organizationId, { branchId: p.branchId, allBranches: false });
      }
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (multiBranch) {
        if (p.allBranches) {
          next.set('all_branches', '1');
          next.delete('branch_id');
        } else if (p.branchId != null) {
          next.delete('all_branches');
          next.set('branch_id', String(p.branchId));
        }
      }
      if (p.serviceId != null) next.set('service_id', String(p.serviceId));
      else next.delete('service_id');
      if (p.trainerUserId != null) {
        next.set('trainer_user_id', String(p.trainerUserId));
        next.delete('trainer_id');
      } else {
        next.delete('trainer_user_id');
        next.delete('trainer_id');
      }
      next.set('schedule_from', clamped.fromDate);
      next.set('schedule_to', clamped.toDate);
      return next;
    });
  }

  function resetScheduleFilters() {
    const multi =
      (widgetConfig?.branches && widgetConfig.branches.length > 1) ||
      (guestShowcaseOrg?.branches && guestBranches.length > 1);
    if (multi && organizationId != null && organizationId > 0) {
      saveBranchFilterPrefs(organizationId, { branchId: null, allBranches: true });
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('service_id');
        next.delete('trainer_user_id');
        next.delete('trainer_id');
        next.delete('schedule_from');
        next.delete('schedule_to');

        if (multi) {
          next.set('all_branches', '1');
          next.delete('branch_id');
        } else {
          next.delete('all_branches');
        }

        return next;
      },
      { replace: true }
    );
  }

  function shiftScheduleRangeByDays(deltaDays: number): void {
    const fromD = new Date(`${range.fromDate}T12:00:00`);
    const toD = new Date(`${range.toDate}T12:00:00`);
    const clamped = clampRange(
      toIsoDate(addDays(fromD, deltaDays)),
      toIsoDate(addDays(toD, deltaDays))
    );
    setPickedIsoDay(clamped.fromDate);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('schedule_from', clamped.fromDate);
      next.set('schedule_to', clamped.toDate);
      next.set('schedule_day', clamped.fromDate);
      return next;
    });
  }

  if (bootstrapHint) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Расписание</h1>
        <p className="text-sm text-[var(--text-muted)]">{bootstrapHint}</p>
      </div>
    );
  }

  if (bootLoading) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <div className="h-8 w-40 animate-pulse rounded bg-[var(--bg-soft)]" />
        <div className="h-9 w-24 animate-pulse rounded-xl bg-[var(--bg-soft)]" />
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 w-full animate-pulse rounded-xl bg-[var(--bg-soft)]" />
          ))}
        </div>
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Расписание</h1>
        <p className="text-sm text-[var(--error-text)]">{bootError}</p>
      </div>
    );
  }

  if (needsBranch) {
    const branchRows =
      widgetConfig?.branches?.map((b) => ({ id: b.id, name: b.name })) ??
      guestBranches.map((b) => ({ id: b.id, name: b.name }));
    const allSalonsParams = new URLSearchParams(searchParams);
    allSalonsParams.set('all_branches', '1');
    allSalonsParams.delete('branch_id');
    const allSalonsSearch = allSalonsParams.toString();
    return (
      <div className="space-y-4 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Выберите {branchLabels.singularLc}</h1>
        <p className="text-sm text-[var(--text-muted)]">
          Несколько {branchLabels.genitivePluralLc}: выберите вариант ниже или откройте общее расписание.
        </p>
        <ul className="flex flex-col gap-2">
          {branchRows.map((b) => (
            <li key={b.id}>
              <BranchPickLink branchId={b.id} organizationId={organizationId}>
                {b.name}
              </BranchPickLink>
            </li>
          ))}
        </ul>
        <p>
          <Link
            className="text-sm font-medium text-[var(--accent)] underline-offset-2 hover:underline"
            to={{ pathname: PATH.schedule, search: allSalonsSearch }}
          >
            Показать расписание по всем {branchLabels.dativePluralLc}
          </Link>
        </p>
      </div>
    );
  }

  const branches =
    widgetConfig?.branches ?? guestBranches.map((b) => ({ id: b.id, name: b.name }));

  const activeBranchFilterLabel =
    isMultiBranch && searchParams.get('all_branches') === '1'
      ? `Все ${branchLabels.pluralLc}`
      : isMultiBranch && effectiveBranchId != null && searchParams.get('all_branches') !== '1'
        ? branches.find((b) => b.id === effectiveBranchId)?.name?.trim() ||
          `${branchLabels.singular} #${effectiveBranchId}`
        : null;

  const showActiveFiltersRow =
    canLoadSlots &&
    (filterServiceId != null || filterTrainerUserId != null || hasActiveCustomDates || activeBranchFilterLabel != null);

  if (isCompactScheduleUi) {
    function compactContent() {
      if (!canLoadSlots) {
        return (
          <div className="flex flex-1 items-center justify-center py-10">
            <p className="text-sm text-[var(--text-muted)]">Нет данных организации для запроса расписания.</p>
          </div>
        );
      }
      if (slotsLoading) {
        return (
          <div className="space-y-3 py-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 animate-pulse rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-sm" />
            ))}
          </div>
        );
      }
      if (slotsError) {
        return (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-10">
            <p className="text-sm text-[var(--error-text)]">{slotsError}</p>
            <Button type="button" variant="secondary" onClick={reloadSlots}>
              Повторить
            </Button>
          </div>
        );
      }
      if (futureByDate.size === 0) {
        return null;
      }

      if (filterServiceId != null) {
        return (
          <div className="flex flex-1 flex-col gap-6">
            {[...futureByDate.entries()].map(([day, daySlots]) => {
              const visibleSlots = daySlots.filter((s) => s.places_left > 0);
              if (visibleSlots.length === 0) return null;
              return (
                <section key={day} className="space-y-2">
                  <h2 className="border-b border-[var(--border)] pb-2 text-sm font-semibold text-[var(--text)]">
                    {formatDayLabel(day)}
                  </h2>
                  {isBookingContour ? (
                    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3">
                      {visibleSlots.map((slot) => (
                        <ScheduleSlotCardCompact key={slot.id} slot={slot} qs={qs} isMultiBranch={isMultiBranch} />
                      ))}
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {visibleSlots.map((slot) => (
                        <li key={slot.id}>
                          <ScheduleSlotCardCompact slot={slot} qs={qs} isMultiBranch={isMultiBranch} />
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
          })}
            </div>
        );
      }

      return null;
    }

    return (
      <div className="flex flex-1 w-full max-w-full min-w-0 flex-col gap-4 overflow-x-hidden bg-[var(--bg-soft)] px-4 pb-3 pt-4">
        <div className="flex items-start justify-between gap-3 pt-1">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold tracking-tight text-[var(--text)]">Расписание</h1>
          </div>
        </div>

        <PromoBanner screen="schedule" />

        {widgetContour === 'mobile' ? (
          <div className="mt-1 grid shrink-0 grid-cols-2 gap-2">
            {isClientPathEnabledByModules(PATH.trainers, effectiveModules, true) ? (
              <Button type="button" variant="secondary" onClick={openTrainers} className="w-full rounded-2xl py-3 text-sm font-semibold">
                Тренеры
              </Button>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`${PATH.services}${suffix}`)}
              className="w-full rounded-2xl py-3 text-sm font-semibold"
            >
              Услуги
            </Button>
          </div>
        ) : (
          <div className="mt-1 shrink-0">
            <Button type="button" onClick={openTrainers} className="w-full rounded-2xl py-3 text-sm font-semibold">
              Записаться к тренеру
            </Button>
          </div>
        )}

        {canLoadSlots ? (
          <div className="flex justify-end pt-1">
            <button
              type="button"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color-mix(in_srgb,var(--accent)_40%,var(--border))] bg-[var(--bg-elevated)] text-[var(--accent)] shadow-[var(--shadow-card)] transition-colors hover:bg-[var(--bg-soft)] active:scale-[0.98]"
              aria-label="Фильтры расписания"
              aria-haspopup="dialog"
              aria-expanded={filtersOpen}
              onClick={() => setFiltersOpen(true)}
            >
              <FilterIcon className="h-5 w-5" />
            </button>
          </div>
        ) : null}

        {showActiveFiltersRow ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="shrink-0 text-xs text-[var(--text-muted)]">Фильтры:</span>
            {activeBranchFilterLabel ? (
              <span className={SCHEDULE_FILTER_CHIP_CLASS}>
                {branchLabels.singular}: {activeBranchFilterLabel}
              </span>
            ) : null}
            {filterServiceId != null ? (
              <span className={SCHEDULE_FILTER_CHIP_CLASS}>{filterServiceName ?? `Услуга #${filterServiceId}`}</span>
            ) : null}
            {filterTrainerUserId != null ? (
              <span className={SCHEDULE_FILTER_CHIP_CLASS}>Тренер: {filterTrainerName}</span>
            ) : null}
            {hasActiveCustomDates ? (
              <span className={SCHEDULE_FILTER_CHIP_CLASS}>{formatPeriodShort(range.fromDate, range.toDate)}</span>
            ) : null}
            <button
              type="button"
              className="shrink-0 rounded-full px-3 py-1.5 text-[11px] font-semibold text-[var(--accent)] transition-colors hover:bg-[var(--bg-soft)]"
              onClick={resetScheduleFilters}
            >
              Сбросить
            </button>
          </div>
        ) : null}

        {canLoadSlots ? (
          <ScheduleFiltersModal
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            branches={branches}
            branchId={branchId}
            allBranchesActive={searchParams.get('all_branches') === '1'}
            serviceId={filterServiceId}
            trainerUserId={filterTrainerUserId}
            fromDate={range.fromDate}
            toDate={range.toDate}
            organizationId={organizationId ?? 0}
            effectiveBranchId={effectiveBranchId}
            orgSlug={orgSlug}
            accessToken={accessToken}
            canLoadServices={canLoadServices && organizationId != null}
            onApply={applyScheduleFilters}
            onReset={resetScheduleFilters}
          />
        ) : null}

        {compactContent()}

        {filterServiceId == null ? (
        <>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="flex h-11 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] text-lg font-bold text-[var(--text-muted)] shadow-sm transition-colors hover:bg-[var(--bg-soft)] hover:text-[var(--text)]"
            onClick={() => scrollStripByPage(-1)}
            aria-label="Назад"
          >
            ‹
          </button>
          <div
            className="flex flex-1 min-w-0 overflow-hidden pb-1 pt-0.5"
            onTouchStart={onDayStripTouchStart}
            onTouchEnd={onDayStripTouchEnd}
          >
            <div
              ref={dayStripRef}
              className="flex flex-1 flex-nowrap gap-2"
            >
            {visibleDays.map((d) => {
            const active = pickedIsoDay === d;
            const today = toIsoDate(new Date());

            return (
              <button
                key={d}
                type="button"
                onClick={() => pickDay(d)}
                className={[
                  'min-w-[3.25rem] flex-1 shrink-0 rounded-xl px-3 py-2.5 text-center transition-colors active:scale-[0.98]',
                  active
                    ? 'border-2 border-[var(--accent)] bg-[color-mix(in_srgb,var(--accent)_14%,var(--bg-elevated))] text-[var(--text)] shadow-sm'
                    : 'border border-[var(--border)] bg-[var(--bg-elevated)] text-[var(--text)] shadow-sm hover:bg-[var(--bg-soft)]'
                ].join(' ')}
              >
                <div className="text-[11px] font-medium text-[var(--color)]">{formatShortWeekdayLower(d)}</div>
                <div className="mt-0.5 text-sm font-bold tabular-nums">{d.slice(8, 10)}.{d.slice(5, 7)}</div>
                {d === today && !active ? (
                  <div className="mx-auto mt-1 h-1 w-1 rounded-full bg-[var(--accent)]" />
                ) : null}
              </button>
            );
          })}
            </div>
          </div>
          <button
            type="button"
            className="flex h-11 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] text-lg font-bold text-[var(--text-muted)] shadow-sm transition-colors hover:bg-[var(--bg-soft)] hover:text-[var(--text)]"
            onClick={() => scrollStripByPage(1)}
            aria-label="Вперёд"
          >
            ›
          </button>
        </div>

        {canLoadSlots && !slotsLoading && !slotsError ? (
          <div className="flex flex-1 flex-col gap-4">
            <h2 className="border-b border-[var(--border)] pb-2.5 text-base font-bold text-[var(--text)]">
              {formatDayLabel(pickedIsoDay)}
            </h2>

            <ul className="flex flex-col gap-3">
              {slotsForPickedDay.map((slot) => (
                <li key={slot.id}>
                  <ScheduleSlotCardCompact slot={slot} qs={qs} isMultiBranch={isMultiBranch} />
                </li>
              ))}
            </ul>
            {slotsForPickedDay.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 py-6">
                <p className="text-center text-sm text-[var(--text-muted)]">На этот день свободных мест нет.</p>
                {nextFreeDay ? (
                  <Button type="button" variant="secondary" onClick={() => jumpToDay(nextFreeDay)}>
                    Ближайшее занятие — {formatDayLabel(nextFreeDay)}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold tracking-tight text-[var(--text)]">Расписание</h1>
          {widgetConfig ? (
            <p className="mt-0.5 text-sm text-[var(--text-muted)]">{widgetConfig.name}</p>
          ) : guestShowcaseOrg ? (
            <p className="mt-0.5 text-sm text-[var(--text-muted)]">{guestShowcaseOrg.name}</p>
          ) : null}
        </div>
        {canLoadSlots ? (
          <Button
            type="button"
            variant="secondary"
            className="mt-0.5 inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-full border border-[var(--border)] px-3 text-[11px] font-semibold shadow-sm"
            onClick={() => setFiltersOpen(true)}
          >
            <FilterIcon className="h-[18px] w-[18px] text-[var(--accent)]" />
            Фильтр
          </Button>
        ) : null}
      </div>

      {showActiveFiltersRow ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="shrink-0 text-xs text-[var(--text-muted)]">Активные фильтры:</span>
          {activeBranchFilterLabel ? (
            <span className={SCHEDULE_FILTER_CHIP_CLASS}>
              {branchLabels.singular}: {activeBranchFilterLabel}
            </span>
          ) : null}
          {filterServiceId != null ? (
            <span className={SCHEDULE_FILTER_CHIP_CLASS}>{filterServiceName ?? `Услуга #${filterServiceId}`}</span>
          ) : null}
          {filterTrainerUserId != null ? (
            <span className={SCHEDULE_FILTER_CHIP_CLASS}>Тренер: {filterTrainerName}</span>
          ) : null}
          {hasActiveCustomDates ? (
            <span className={SCHEDULE_FILTER_CHIP_CLASS}>{formatPeriodShort(range.fromDate, range.toDate)}</span>
          ) : null}
          <button
            type="button"
            className="shrink-0 rounded-full px-3 py-1.5 text-[11px] font-semibold text-[var(--accent)] transition-colors hover:bg-[var(--bg-soft)]"
            onClick={resetScheduleFilters}
          >
            Сбросить всё
          </button>
        </div>
      ) : null}

      {canLoadSlots ? (
        <ScheduleFiltersModal
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          branches={branches}
          branchId={branchId}
          allBranchesActive={searchParams.get('all_branches') === '1'}
          serviceId={filterServiceId}
          trainerUserId={filterTrainerUserId}
          fromDate={range.fromDate}
          toDate={range.toDate}
          organizationId={organizationId ?? 0}
          effectiveBranchId={effectiveBranchId}
          orgSlug={orgSlug}
          accessToken={accessToken}
          canLoadServices={canLoadServices && organizationId != null}
          onApply={applyScheduleFilters}
          onReset={resetScheduleFilters}
        />
      ) : null}

      {!canLoadSlots ? (
        <p className="text-sm text-[var(--text-muted)]">Нет данных организации для запроса расписания.</p>
      ) : slotsLoading ? (
        <div className="space-y-8">
          {Array.from({ length: 3 }).map((_, di) => (
            <div key={di} className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)]">
              <div className="border-b border-[var(--border)] bg-[var(--bg-soft)]/90 px-4 py-3">
                <div className="h-4 w-48 animate-pulse rounded bg-[var(--bg-soft)]" />
              </div>
              <ul className="divide-y divide-[var(--border)]">
                {Array.from({ length: di === 0 ? 4 : di === 1 ? 3 : 2 }).map((_, si) => (
                  <li key={si} className="flex items-center justify-between gap-4 px-4 py-3.5">
                    <div className="space-y-1.5">
                      <div className="h-4 w-40 animate-pulse rounded bg-[var(--bg-soft)]" />
                      <div className="h-3 w-28 animate-pulse rounded bg-[var(--bg-soft)]" />
                    </div>
                    <div className="h-6 w-20 animate-pulse rounded bg-[var(--bg-soft)]" />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : slotsError ? (
        <div className="flex flex-col items-center gap-3 py-4">
          <p className="text-sm text-[var(--error-text)]">{slotsError}</p>
          <Button type="button" variant="secondary" onClick={reloadSlots}>
            Повторить
          </Button>
        </div>
      ) : futureByDate.size === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--border)] bg-[var(--bg-soft)]/40 px-4 py-12 text-center">
          <p className="text-sm text-[var(--text-muted)]">
            Нет доступных слотов за выбранный период. Измените фильтр или даты.
          </p>
          <Button type="button" variant="secondary" className="mt-4" onClick={() => shiftScheduleRangeByDays(7)}>
            Следующая неделя
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {[...futureByDate.entries()].map(([day, daySlots]) => {
            const visibleSlots = daySlots.filter((s) => s.places_left > 0);
            if (visibleSlots.length === 0) return null;
            return (
              <section key={day} className="space-y-2">
                <h2 className="border-b border-[var(--border)] pb-2 text-sm font-semibold text-[var(--text)]">
                  {formatDayLabel(day)}
                </h2>
                {isBookingContour ? (
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3">
                    {visibleSlots.map((slot) => (
                      <ScheduleSlotCardCompact key={slot.id} slot={slot} qs={qs} isMultiBranch={isMultiBranch} />
                    ))}
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {visibleSlots.map((slot) => (
                      <li key={slot.id}>
                        <ScheduleSlotCardCompact slot={slot} qs={qs} isMultiBranch={isMultiBranch} />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
